import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { useTRPC } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { ProviderInviteShell } from "./components/provider-invite-shell";
import { InvitationLoadingCard } from "./components/invitation-loading-card";
import { InvitationInvalidCard } from "./components/invitation-invalid-card";
import { InvitationExpiredCard } from "./components/invitation-expired-card";
import { InvitationAcceptedCard } from "./components/invitation-accepted-card";
import { InvitationSignInCard } from "./components/invitation-sign-in-card";
import { InvitationPendingMismatchCard } from "./components/invitation-pending-mismatch-card";
import { InvitationPendingAcceptCard } from "./components/invitation-pending-accept-card";
import { InvitationRetryCard } from "./components/invitation-retry-card";
import { useSupabaseSessionEmail } from "./hooks/use-supabase-session-email";
import { emailsMatch } from "./utils/invite-redirect";

// How long the "you're in" confirmation shows before auto-navigating away.
const ACCEPT_REDIRECT_DELAY_MS = 1500;

export function ProviderInviteAcceptPage({ token }: { token?: string }) {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { email: signedInEmail, isLoading: isSessionLoading } =
    useSupabaseSessionEmail();
  const [justAccepted, setJustAccepted] = useState(false);

  const invitationOptions =
    trpc.organizationBusinessUnitAffiliation.getInvitationByToken.queryOptions({
      token: token ?? "",
    });
  const invitationQuery = useQuery({
    ...invitationOptions,
    enabled: Boolean(token),
    retry: false,
  });

  const acceptMutation = useMutation(
    trpc.organizationBusinessUnitAffiliation.acceptInvitation.mutationOptions({
      onSuccess: () => {
        setJustAccepted(true);
      },
      onError: () => {
        // Re-check invitation state regardless of outcome: a failure may mean
        // the invitation moved to expired/removed since the page loaded, and
        // the corresponding card (not just an inline error) should take over.
        void queryClient.invalidateQueries({
          queryKey: invitationOptions.queryKey,
        });
      },
    }),
  );

  useEffect(() => {
    if (!justAccepted) {
      return;
    }
    const timeout = setTimeout(() => {
      navigate({ to: "/provider/home" });
    }, ACCEPT_REDIRECT_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [justAccepted, navigate]);

  if (!token) {
    return (
      <ProviderInviteShell>
        <InvitationInvalidCard />
      </ProviderInviteShell>
    );
  }

  if (invitationQuery.isPending) {
    return (
      <ProviderInviteShell>
        <InvitationLoadingCard />
      </ProviderInviteShell>
    );
  }

  if (invitationQuery.isError) {
    const isNotFound =
      invitationQuery.error instanceof TRPCClientError &&
      invitationQuery.error.data?.code === "NOT_FOUND";
    return (
      <ProviderInviteShell>
        {isNotFound ? (
          <InvitationInvalidCard />
        ) : (
          <InvitationRetryCard
            onRetry={() => void invitationQuery.refetch()}
            isRetrying={invitationQuery.isFetching}
          />
        )}
      </ProviderInviteShell>
    );
  }

  const invitation = invitationQuery.data;

  if (invitation.state === "expired") {
    return (
      <ProviderInviteShell>
        <InvitationExpiredCard organizationName={invitation.organizationName} />
      </ProviderInviteShell>
    );
  }

  if (invitation.state === "accepted" && !justAccepted) {
    return (
      <ProviderInviteShell>
        <InvitationAcceptedCard
          token={token}
          businessUnitName={invitation.businessUnitName}
          isSignedIn={Boolean(signedInEmail)}
        />
      </ProviderInviteShell>
    );
  }

  // state === "pending" (or we just accepted it ourselves this session)
  if (isSessionLoading) {
    return (
      <ProviderInviteShell>
        <InvitationLoadingCard />
      </ProviderInviteShell>
    );
  }

  if (!signedInEmail) {
    return (
      <InvitationSignInCard
        organizationName={invitation.organizationName}
        businessUnitName={invitation.businessUnitName}
        invitedEmail={invitation.email}
      />
    );
  }

  if (!justAccepted && !emailsMatch(signedInEmail, invitation.email)) {
    return (
      <ProviderInviteShell>
        <InvitationPendingMismatchCard
          token={token}
          invitedEmail={invitation.email}
          signedInEmail={signedInEmail}
        />
      </ProviderInviteShell>
    );
  }

  return (
    <ProviderInviteShell>
      <InvitationPendingAcceptCard
        organizationName={invitation.organizationName}
        businessUnitName={invitation.businessUnitName}
        onAccept={() => acceptMutation.mutate({ token })}
        isAccepting={acceptMutation.isPending}
        errorMessage={acceptMutation.error?.message}
        justAccepted={justAccepted}
      />
    </ProviderInviteShell>
  );
}
